import { useState } from "react";
import { RouteComponentProps } from "react-router";
import "./login.scss";
import { AuthReason } from "models/settings";
import { loginAuthorize } from "core/auth0";
import * as Storage from "core/storage";
import { useInitialEffect } from "core/react-utils";
import { AppStore } from "stores";
import { Button, Form, Message } from "semantic-ui-react";
import LoginForm from "models/forms/login-form";
import { observer } from "mobx-react-lite";
import { Link } from "react-router-dom";
import { getUrlParam } from "../../core/utils";

interface Props extends RouteComponentProps<any> {}

const loginForm = new LoginForm();

const LoginPage = observer((props: Props) => {
  const [loginFailed, setLoginFailed] = useState(false);

  useInitialEffect(() => {
    loginForm.clearFormData();

    const slackMemberId = getUrlParam("slackMemberId");
    Storage.setSlackMemberId(slackMemberId);

    if (window.location.pathname != "/login" && window.location.pathname != "/") {
      let redirect = window.location.pathname + window.location.search;
      Storage.setAuthInfo(redirect, AuthReason.Login);
    } else {
      Storage.setAuthInfo(null, AuthReason.Login);
    }

    loginForm.slackMemberId = slackMemberId;
  });

  const login = async () => {
    try {
      await AppStore.login(loginForm);

      const { url } = Storage.popAuthInfo();

      if (!url) {
        props.history.replace("/" + AppStore.selectedAccountName + "/test-runs");
      } else {
        props.history.replace(url);
      }
    } catch (error) {
      if (error == "login-failed") {
        setLoginFailed(true);
      }
    }
  };

  return (
    <div id="login" className="login-page">
      <div className="login-area">
        <div className="login-content hide-on-mobile">
          <div className="content left-content pd-20">
            <div className="logo">
              <img src="/img/testery-black-wide-logo.png" alt="Testery" />
            </div>
            <h2>Welcome back!</h2>
            <div className="item">
              <div className="heading">
                <i className="fas fa-check"></i>&nbsp;&nbsp;
                <span>Auto-scaling test runners for 2-4X faster test runs</span>
              </div>
              <p>
                Scale to hundreds or even thousands of concurrent test runners without performance bottlenecks.
              </p>
            </div>
            <div className="item">
              <div className="heading">
                <i className="fas fa-check"></i>&nbsp;&nbsp;
                <span>Flexible test running options</span>
              </div>
              <p>
                Run the right tests at the right time whether that's on a schedule, on-demand, on pull requests, or from your CI/CD. Automatically re-run failed tests and automatically quarantine flaky tests.
              </p>
            </div>
            <div className="item">
              <div className="heading">
                <i className="fas fa-check"></i>&nbsp;&nbsp;
                <span>Logging, Analytics, Videos, and More</span>
              </div>
              <p>
                See the results in the Testery dashboard or in your CI/CD pipeline. Testery also provides insights into your test suite, including flaky test detection and test run performance.
              </p>
            </div>
          </div>
        </div>
        <div className="login-cta">
          <div className="content">
            <div className="header">
              Log in to your account
            </div>

            <div className="login-overview-cta">
              <div className="cta">
                <div className="buttons">
                  <a className="github" onClick={() => loginAuthorize("github")}>
                    <i className="fab fa-github"></i>
                    <div>Log In with GitHub</div>
                  </a>
                  <br />
                  <a className="bitbucket" onClick={() => loginAuthorize("bitbucket")}>
                    <i className="fab fa-bitbucket"></i>
                    <div>Log In with BitBucket</div>
                  </a>
                  <br />
                </div>
                <div className="cta">
                  <div className="or">
                    <hr />
                  </div>
                  <Form id="login-form" error={loginFailed}>
                    <Form.Input
                      error={loginForm.getErrorText("email")}
                      placeholder="Email"
                      icon="envelope"
                      iconPosition="left"
                      type="text"
                      value={loginForm.email}
                      onChange={loginForm.onChange("email")}
                      autoComplete="username"
                    />
                    <Form.Input
                      placeholder="Password"
                      icon="key"
                      iconPosition="left"
                      type="password"
                      error={loginForm.getErrorText("password")}
                      value={loginForm.password}
                      onChange={loginForm.onChange("password")}
                      autoComplete="current-password"
                    />
                    <Button
                      loading={loginForm.isSubmitting}
                      type="submit"
                      className="email-login-btn"
                      onClick={login}
                    >
                      Log In with Email
                    </Button>
                    <Message error content="Invalid email or password" />
                  </Form>
                  <div className="forgot-password">
                    <Link to="/forgot-password">Forgot Password?</Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="signup-link">
              Don't have an account? <Link to="/create-account">Sign up here.</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default LoginPage;
